import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { CheckCircle, CreditCard, Calendar, ArrowRight } from 'lucide-react';

export default function MembershipNextSteps() {
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationId, plan, memberType } = location.state || {};

  // Calendly scheduling link
  const calendlyLink = 'https://calendly.com/xavier-csed/30min';

  const handleCheckout = () => {
    navigate('/membership/payment', {
      state: {
        applicationId,
        plan,
        memberType
      }
    });
  };

  const handleMeetExecutive = () => {
    // Open Calendly in new tab
    window.open(calendlyLink, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="bg-gradient-to-r from-[#1E3A5F] to-[#1E3A5F]/90 text-white py-16 pt-32">
        <div className="container mx-auto px-4 text-center">
          <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
          <h1 className="text-4xl font-bold mb-4">Application Submitted Successfully!</h1>
          <p className="text-gray-200 max-w-2xl mx-auto">
            Thank you for applying to become a CSED member. Your application has been received.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Application Summary */}
        <Card className="max-w-3xl mx-auto mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-[#1E3A5F]">Application Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              {applicationId && (
                <div>
                  <span className="font-semibold text-gray-700">Application ID:</span>
                  <p className="text-gray-600 font-mono text-xs">{applicationId.substring(0, 8).toUpperCase()}</p>
                </div>
              )}
              <div>
                <span className="font-semibold text-gray-700">Membership Type:</span>
                <p className="text-gray-600 capitalize">{memberType || 'N/A'}</p>
              </div>
              {plan && (
                <>
                  <div>
                    <span className="font-semibold text-gray-700">Selected Plan:</span>
                    <p className="text-gray-600">{plan.plan_name}</p>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700">Plan Details:</span>
                    <p className="text-gray-600">{plan.description}</p>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Next Steps Options */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-[#1E3A5F] mb-6 text-center">
            Choose Your Next Step
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Option 1: Proceed to Checkout */}
            <Card className="border-2 border-[#E87722] hover:shadow-lg transition-all cursor-pointer group">
              <CardContent className="p-6">
                <div className="flex flex-col items-center text-center h-full">
                  <div className="w-16 h-16 bg-[#E87722] rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <CreditCard className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1E3A5F] mb-2">
                    Proceed to Checkout
                  </h3>
                  <p className="text-gray-600 mb-4 flex-grow">
                    Complete your membership registration by paying the registration fee and your annual membership fee.
                  </p>
                  <Button
                    onClick={handleCheckout}
                    className="w-full bg-[#E87722] hover:bg-[#d66a1a] text-white"
                  >
                    Go to Payment
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Option 2: Meet CSED Executive */}
            <Card className="border-2 border-[#1E3A5F] hover:shadow-lg transition-all cursor-pointer group">
              <CardContent className="p-6">
                <div className="flex flex-col items-center text-center h-full">
                  <div className="w-16 h-16 bg-[#1E3A5F] rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Calendar className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1E3A5F] mb-2">
                    Meet CSED Executive
                  </h3>
                  <p className="text-gray-600 mb-4 flex-grow">
                    Schedule a meeting with our executive team to discuss your application, membership benefits, and how CSED can support your goals.
                  </p>
                  <Button
                    onClick={handleMeetExecutive}
                    variant="outline"
                    className="w-full border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white"
                  >
                    Book Appointment
                    <Calendar className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Additional Information */}
          <div className="mt-8 p-6 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="font-semibold text-[#1E3A5F] mb-2">What happens next?</h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start">
                <span className="text-[#E87722] mr-2">•</span>
                <span>If you proceed to checkout, your membership will be activated upon successful payment.</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#E87722] mr-2">•</span>
                <span>If you choose to meet with an executive, they will review your application and guide you through the next steps.</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#E87722] mr-2">•</span>
                <span>You can always come back to complete your payment or schedule a meeting later.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
